import { makeStyles } from '@material-ui/core/styles'
import { createStyles, Theme } from '@material-ui/core'
import { elevatedSurfaceBoxShadow, transition } from '../../../shared/style-settings'

const mobileBreakpoint = 'sm'
const extraSmallBreakpoint = 400
const wideDetailsBreakpoint = 1300

export const useOrdersListItemStyles = makeStyles((theme: Theme) =>
  createStyles({
    orderCardDisclosure: {
      width: '100%',
      display: 'block',
    },
    orderCard: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.shape.borderRadius * 2,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      boxShadow: elevatedSurfaceBoxShadow,
      transition: transition,
      '&:hover': {
        borderColor: theme.palette.primary.main,
        boxShadow: elevatedSurfaceBoxShadow,
      },
      [theme.breakpoints.down(mobileBreakpoint)]: {
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
        borderRadius: theme.shape.borderRadius,
      },
    },
    orderCardSkeleton: {
      pointerEvents: 'none',
      '&:hover': {
        borderColor: theme.palette.divider,
        boxShadow: elevatedSurfaceBoxShadow,
      },
    },
    skeletonThumb: {
      width: 80,
      height: 80,
      borderRadius: theme.shape.borderRadius,
      flexShrink: 0,
      transform: 'none',
      [theme.breakpoints.down(mobileBreakpoint)]: {
        width: 56,
        height: 56,
      },
      [theme.breakpoints.down(extraSmallBreakpoint)]: {
        width: '100%',
        height: 72,
        alignSelf: 'stretch',
      },
    },
    orderCardSummary: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      width: '100%',
      padding: 0,
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      textAlign: 'left',
      color: 'inherit',
      font: 'inherit',
      listStyle: 'none',
      '&::-webkit-details-marker': {
        display: 'none',
      },
      [theme.breakpoints.down(extraSmallBreakpoint)]: {
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        alignItems: 'stretch',
      },
    },
    orderCardSummaryRight: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      flexShrink: 0,
      '& .MuiChip-root': {
        fontWeight: 500,
        fontSize: '0.8125rem',
      },
      [theme.breakpoints.down(extraSmallBreakpoint)]: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: theme.spacing(1),
        '& .MuiChip-root': {
          fontSize: '0.75rem',
          height: 24,
          '& .MuiChip-label': {
            paddingLeft: 8,
            paddingRight: 8,
          },
        },
      },
      [theme.breakpoints.down(mobileBreakpoint)]: {
        '& .MuiChip-root': {
          fontSize: '0.75rem',
          height: 24,
          '& .MuiChip-label': {
            paddingLeft: 8,
            paddingRight: 8,
          },
        },
      },
    },
    orderCardExpanded: {
      width: '100%',
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(2),
      borderTop: `1px solid ${theme.palette.divider}`,
      [theme.breakpoints.down(mobileBreakpoint)]: {
        marginTop: theme.spacing(1.5),
        paddingTop: theme.spacing(1.5),
      },
    },
    orderCardThumb: {
      width: 80,
      height: 80,
      marginRight: theme.spacing(1),
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.grey[200],
      flexShrink: 0,
      overflow: 'hidden',
      display: 'flex',
      '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      },
      [theme.breakpoints.down(mobileBreakpoint)]: {
        width: 56,
        height: 56,
        marginRight: theme.spacing(1.25),
      },
      [theme.breakpoints.down(extraSmallBreakpoint)]: {
        width: '100%',
        height: 72,
        marginRight: 0,
        alignSelf: 'stretch',
      },
    },
    orderCardBody: {
      flex: 1,
      minWidth: 0,
      paddingLeft: 0,
      textAlign: 'left',
      overflow: 'hidden',
      [theme.breakpoints.down(extraSmallBreakpoint)]: {
        minWidth: '100%',
        width: '100%',
      },
    },
    orderCardTitle: {
      fontWeight: 600,
      fontSize: '1rem',
      marginBottom: theme.spacing(0.5),
      textAlign: 'left',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      [theme.breakpoints.down(mobileBreakpoint)]: {
        fontSize: '0.9375rem',
      },
    },
    orderCardTitleLink: {
      color: 'inherit',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    orderCardThumbLink: {
      color: 'inherit',
      textDecoration: 'none',
    },
    orderCardMeta: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(0.25),
      textAlign: 'left',
      [theme.breakpoints.down(mobileBreakpoint)]: {
        fontSize: '0.8125rem',
      },
    },
    orderCardViewOrderLink: {
      display: 'inline-block',
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(0.25),
      fontSize: '0.875rem',
      fontWeight: 600,
      color: theme.palette.primary.main,
      textAlign: 'left',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
      [theme.breakpoints.down(mobileBreakpoint)]: {
        fontSize: '0.8125rem',
      },
    },
    orderCardDescription: {
      fontSize: '0.8125rem',
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(0.25),
      textAlign: 'left',
      lineHeight: 1.4,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      [theme.breakpoints.down(mobileBreakpoint)]: {
        fontSize: '0.75rem',
        marginTop: theme.spacing(0.25),
      },
    },
    orderCardDetails: {
      width: '100%',
      [`@media (min-width:${wideDetailsBreakpoint}px)`]: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        columnGap: theme.spacing(6),
        rowGap: theme.spacing(1.5),
        alignItems: 'start',
      },
    },
    orderCardDetailsColumn: {
      minWidth: 0,
    },
    orderCardSection: {
      marginBottom: theme.spacing(1.5),
      [`@media (min-width:${wideDetailsBreakpoint}px)`]: {
        marginBottom: 0,
      },
      '&:last-child': {
        marginBottom: 0,
      },
    },
    orderCardSectionTitle: {
      fontWeight: 600,
      fontSize: '0.875rem',
      marginBottom: theme.spacing(0.75),
      textAlign: 'left',
    },
    orderCardDetailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: theme.spacing(2),
      fontSize: '0.8125rem',
      fontWeight: 600,
      marginBottom: theme.spacing(0.5),
      textAlign: 'left',
      [theme.breakpoints.down(mobileBreakpoint)]: {
        flexDirection: 'column',
        gap: theme.spacing(0.25),
        fontSize: '0.75rem',
        marginBottom: theme.spacing(0.75),
      },
    },
    orderCardDetailLabel: {
      color: theme.palette.text.secondary,
      flexShrink: 0,
      fontWeight: 600,
    },
    orderCardDetailValue: {
      color: theme.palette.text.primary,
      textAlign: 'right',
      wordBreak: 'break-word',
      [theme.breakpoints.down(mobileBreakpoint)]: {
        textAlign: 'left',
      },
    },
    orderCardAmountBlock: {
      marginTop: theme.spacing(1.5),
      padding: theme.spacing(1.5),
      backgroundColor: theme.palette.grey[50],
      borderRadius: theme.shape.borderRadius,
      [`@media (min-width:${wideDetailsBreakpoint}px)`]: {
        marginTop: 0,
      },
      [theme.breakpoints.down(mobileBreakpoint)]: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(1.25),
      },
    },
    orderCardAmountRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '0.875rem',
      marginBottom: theme.spacing(0.5),
    },
    orderCardAmountTotal: {
      marginTop: theme.spacing(0.75),
      paddingTop: theme.spacing(0.75),
      borderTop: `1px solid ${theme.palette.divider}`,
      fontWeight: 700,
      fontSize: '1rem',
    },
  })
)
